import { rutClean } from 'rut-helpers';
import { RequestGenerateSendOtp } from '../interfaces/generate-send-otp.interface';
import { PurposesByType, PurposeUI } from '../interfaces/get-purposes';
import { GtmExtras } from '../interfaces/gtm.interface';
import { SalesLevelModel } from '../interfaces/initial-parameters.interface';
import { RUT_PJ_THRESHOLD } from './constants';
import { NotificationType } from './enums';

export function buildRequestGenerateOtp(
	rut: string,
	newDestination?: string
): RequestGenerateSendOtp {
	return {
		rutCliente: rut,
		tipoNotificacion: NotificationType.DEFAULT,
		...(newDestination && { nuevoDestino: newDestination }),
	};
}

export function buildUrlSimulator(url: string, uuid: string, id: string): string {
	return `${url}?token=${uuid}&id=${id}`;
}

export function pluralizeAttempts(value: string): string {
	return value === '1' ? `Te queda ${value} intento.` : `Te quedan ${value} intentos.`;
}

export function timerFormat(totalSeconds: number): string {
	const hrs = Math.floor(totalSeconds / 3600);
	const min = Math.floor((totalSeconds % 3600) / 60);
	const sec = totalSeconds % 60;

	return hrs > 0
		? `${hrs}:${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
		: `${min}:${sec.toString().padStart(2, '0')}`;
}

export function isLegalEntity(rut: string): boolean {
	const rutNumber = Number(rutClean(rut).slice(0, -1));
	return rutNumber > RUT_PJ_THRESHOLD;
}

export function formatRutWithDash(rut: string): string {
	const cleanRut = rutClean(rut);
	const body = cleanRut.slice(0, -1);
	const dv = cleanRut.slice(-1);
	return `${body}-${dv}`;
}

export function findSelectedSalesRange(
	salesLevels: SalesLevelModel[],
	selectedLevel: string
): string {
	return salesLevels.find(item => item.level === selectedLevel)?.range ?? '';
}

export function selectIdFormPurpose(isPJ: boolean, purposes: PurposesByType): string {
	return (isPJ ? purposes.pj.idForm : purposes.png.idForm);
}

export function selectPurposes(isPJ: boolean, purposes: PurposesByType): PurposeUI[] {
	return (isPJ ? purposes?.pj.purposes : purposes?.png.purposes);
}

export async function mapToGtmExtras(
	rut: string,
	email: string,
	level: string
): Promise<Partial<GtmExtras>> {
	return {
		'rut-empresa': convertToBase64(rut),
		sha256_mail_cta_digital: await sha256(email),
		monto: level,
	};
}

function convertToBase64(value: string): string {
	const bytes = new TextEncoder().encode(value);
	let binary = '';
	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}
	return window.btoa(binary);
}

async function sha256(value: string): Promise<string> {
	const data = new TextEncoder().encode(value.trim().toLowerCase());
	const hashBuffer = await crypto.subtle.digest('SHA-256', data);

	return Array.from(new Uint8Array(hashBuffer))
		.map(b => b.toString(16).padStart(2, '0'))
		.join('');
}

export function buildWhatsAppUrl(
	baseUrl: string,
	phoneNumber: string,
	clientId: string,
	message: string
): string {
	return `${baseUrl}?phoneTo=${phoneNumber}&message=${encodeURIComponent(message)}&clientId=${clientId}`;
}
